/**
 * Reddit Pixel conversion tracking utilities.
 *
 * Base pixel is loaded in index.html (production only).
 * This module provides typed wrappers for firing conversion events
 * and Advanced Matching (hashed email/userId for better attribution).
 *
 * ─────────────────────────── Use trackUserEvent() ───────────────────────────
 *
 * trackUserEvent() is the single public API for component-level tracking. It
 * consults a registered consent checker and:
 *
 *   - FIRES for anonymous visitors (no logged-in user)
 *   - FIRES for logged-in users who granted data_collection_consent
 *   - NO-OPS for logged-in users who opted out
 *
 * This matters because the Reddit Pixel sets a browser cookie that persists
 * across auth state. Even "anonymous" events like article views get correlated
 * back to a logged-in user via that cookie — so firing an unguarded event
 * while an opted-out user is viewing the page would leak their activity to
 * Reddit, regardless of whether the event payload itself contains PII.
 *
 * trackRedditEvent() is EXPORTED but should be treated as module-internal.
 * Tests use it directly to verify the raw pixel call shape. Components must
 * use trackUserEvent() for behavioral/engagement events so the consent gate
 * is always applied.
 *
 * trackConversion() is the deliberate exception: a no-PII hard-conversion
 * ping (e.g. SignUp) that is intentionally NOT behind the consent gate, so
 * Reddit actually receives the event it optimizes campaigns toward. The
 * privacy-sensitive identity correlation (identifyRedditUser, Advanced
 * Matching) stays consent-gated at its call sites. See trackConversion's
 * own docblock for the full rationale.
 *
 * The consent checker is registered once at app startup via configureConsent()
 * from providers.tsx to avoid a circular import between this util and
 * auth.store (which imports identifyRedditUser from here).
 */

// Reddit Pixel exposes a `rdt(...)` callable on window plus a `__REDDIT_PIXEL_ID`
// string set by index.html. Declare both as typed globals on the Window type.
type RdtFn = ((...args: unknown[]) => void) & {
  callQueue?: unknown[];
};
declare global {
  interface Window {
    rdt?: RdtFn;
    __REDDIT_PIXEL_ID?: string;
  }
}

// Single source of truth: set in index.html before the pixel loads
const PIXEL_ID: string = window.__REDDIT_PIXEL_ID || 'a2_iq6hzwbko7ss';

function getRdt(): RdtFn | null {
  return typeof window !== 'undefined' ? (window.rdt ?? null) : null;
}

/**
 * SHA-256 hash a string (for Advanced Matching).
 * Reddit requires email and externalId to be SHA-256 hashed.
 */
async function sha256(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Standard Reddit pixel event names + Custom (which uses customEventName).
 * See https://business.reddithelp.com/s/article/manual-conversion-events
 */
export type RedditEventName =
  | 'PageVisit'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'Purchase'
  | 'Lead'
  | 'SignUp'
  | 'Custom';

export interface RedditEventData {
  currency?: string;
  value?: number;
  itemCount?: number;
  /** Required when event === 'Custom'. Reddit groups Custom events by this. */
  customEventName?: string;
}

/**
 * Data payload specifically for Custom events — customEventName is required.
 * Used by the function overloads to enforce the requirement at compile time.
 */
export type RedditCustomEventData = RedditEventData & { customEventName: string };

/**
 * Private implementation that actually calls rdt('track', ...). Widened
 * signature (no Custom/non-Custom split) so it can be called from either
 * public entry point without type casts. The public overloads below enforce
 * the Custom-requires-customEventName constraint at call sites.
 */
function fireRawEvent(event: RedditEventName, data?: RedditEventData): void {
  const rdt = getRdt();
  if (!rdt) return;
  if (data) {
    rdt('track', event, data);
  } else {
    rdt('track', event);
  }
}

/**
 * INTERNAL: fires the raw pixel call without a consent check.
 *
 * Exported for test access only. Components MUST use trackUserEvent() so the
 * GDPR/CCPA consent gate is always applied — see the module docblock for why.
 *
 * Function overloads enforce that Custom events carry a customEventName at
 * compile time.
 */
export function trackRedditEvent(event: 'Custom', data: RedditCustomEventData): void;
export function trackRedditEvent(
  event: Exclude<RedditEventName, 'Custom'>,
  data?: RedditEventData,
): void;
export function trackRedditEvent(event: RedditEventName, data?: RedditEventData): void {
  fireRawEvent(event, data);
}

// ─────────────────────────── Consent gate ───────────────────────────

/**
 * The consent checker is registered once at app startup so this util doesn't
 * have to import auth.store directly (which would create a circular import,
 * since auth.store imports from this file).
 *
 * Default is "no consent" — the wrapper short-circuits if configureConsent()
 * was never called. That way a forgotten setup never accidentally leaks
 * tracking data.
 *
 * The registered checker should return true for:
 *   - Anonymous visitors (no logged-in user), where consent is inapplicable
 *   - Logged-in users who granted data_collection_consent
 * And false for logged-in users who explicitly opted out.
 */
let consentChecker: () => boolean = () => false;

/**
 * Register the consent check. Call this once during app bootstrap (in
 * providers.tsx) with a closure that reads the current user from the auth
 * store and respects data_collection_consent for identified users while
 * allowing anonymous tracking.
 */
export function configureConsent(checker: () => boolean): void {
  consentChecker = checker;
}

/**
 * Whether the current visitor may be tracked behaviorally (anonymous visitors
 * yes; logged-in users only if they granted data_collection_consent). Exposed
 * so the GA4 helper (utils/ga.ts) can reuse the SAME consent decision as the
 * Reddit pixel instead of duplicating the auth-store read + circular-import
 * dance. Mirrors the gate inside trackUserEvent.
 */
export function hasTrackingConsent(): boolean {
  return consentChecker();
}

/**
 * Track a Reddit conversion event, gated on consent.
 *
 * Safe to use for both anonymous visitors (the gate allows anonymous
 * tracking) and logged-in users (the gate respects data_collection_consent).
 * This is the function components should use for behavioral/engagement
 * events. The one exception is hard conversion events — see trackConversion.
 *
 * Function overloads enforce that Custom events carry a customEventName at
 * compile time.
 */
export function trackUserEvent(event: 'Custom', data: RedditCustomEventData): void;
export function trackUserEvent(
  event: Exclude<RedditEventName, 'Custom'>,
  data?: RedditEventData,
): void;
export function trackUserEvent(event: RedditEventName, data?: RedditEventData): void {
  if (!consentChecker()) return;
  fireRawEvent(event, data);
}

/**
 * Hard conversion events the business optimizes ad spend toward. This
 * is intentionally narrower than RedditEventName: behavioral/engagement
 * events (PageVisit, ViewContent, Search, AddToCart, AddToWishlist) MUST
 * stay on the consent-gated trackUserEvent path. Only events listed here
 * are allowed to bypass the gate via trackConversion.
 */
export type RedditConversionEvent = 'SignUp' | 'Lead' | 'Purchase';

/**
 * Track a hard conversion event (e.g. SignUp) WITHOUT the behavioral
 * consent gate.
 *
 * Why this is privacy-safe and intentionally un-gated:
 *
 *   - The payload carries NO PII. We pass only the standard event name
 *     (and optional currency/value/itemCount), never email or user id.
 *   - Advanced Matching stays gated. identifyRedditUser() (the call that
 *     attaches a hashed email/externalId to the Reddit cookie) remains
 *     behind data_collection_consent at its call sites. Without it this
 *     event is an anonymous-equivalent conversion count.
 *   - It is analogous to the unconditional PageVisit fired by the base
 *     pixel in index.html: a single, non-behavioral signal marking that
 *     a conversion happened, not ongoing activity profiling.
 *
 * This exists because Reddit (and any) ad optimizer can only optimize a
 * campaign toward an event it actually receives. Gating SignUp behind
 * an optional data_collection_consent checkbox (default false) meant
 * Reddit never received it, so it could not be selected as a conversion
 * goal.
 *
 * The `event` parameter is constrained to RedditConversionEvent so a
 * future caller cannot accidentally pass a behavioral event like
 * PageVisit or ViewContent and bypass the consent gate.
 */
export function trackConversion(
  event: RedditConversionEvent,
  data?: Omit<RedditEventData, 'customEventName'>,
): void {
  fireRawEvent(event, data);
}

// ─────────────────────────── Identify (Advanced Matching) ───────────────────

/**
 * Re-initialize the pixel with hashed user identity for Advanced Matching.
 * Call after login/register to improve Reddit's attribution match rate.
 *
 * NOTE: callers must gate this on data_collection_consent themselves — this
 * function does NOT consult the consent checker because it's typically called
 * during the login/register flow, and the brand-new user object from the
 * response is what we need to consult (bypassing the auth store avoids any
 * reasoning about store-update timing). See auth.store.ts for the call site.
 */
export async function identifyRedditUser(email: string, userId?: number | string) {
  const rdt = getRdt();
  if (!rdt) return;

  const opts: Record<string, string> = {};
  opts.email = await sha256(email);
  if (userId) {
    opts.externalId = await sha256(String(userId));
  }

  rdt('init', PIXEL_ID, opts);
}

/**
 * Clear the pixel's Advanced Matching identifiers. Call on logout so that
 * events fired after logout don't remain correlated to the previous user's
 * hashed identity. Re-initializes the pixel with empty opts, which resets
 * the email/externalId for subsequent tracking calls on the same browser
 * session.
 *
 * NOTE: the underlying Reddit Pixel cookie (e.g., `_rdt_uuid`) is set by
 * Reddit and may persist regardless — this function only clears the
 * client-side identity we passed in. For stricter clearing you'd also need
 * to delete the cookie, which is typically impractical across domains.
 * Resetting the Advanced Matching keys is what affects Reddit's
 * server-side attribution for future events.
 */
export function clearRedditUser(): void {
  const rdt = getRdt();
  if (!rdt) return;
  rdt('init', PIXEL_ID, {});
}
